import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { getAllRDV } from "../../services/rdvAgendaServices";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useScrollToTop from "../../hooks/useScrollToTop";
import ReservationRDVModal from "./ReservationRDVModal";
import { LanguageContext } from "../../contexts/LanguageContext";
import { format, parseISO, addDays } from "date-fns";
import { fr, enGB } from "date-fns/locale";
import {
  FaRegCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaSyncAlt,
  FaChevronRight,
  FaArrowLeft,
} from "react-icons/fa";

const isoToday = () => new Date().toISOString().slice(0, 10);
const toHHMM = (s) => String(s || "").slice(0, 5);

const toISODateSafe = (d) => {
  try {
    return format(d, "yyyy-MM-dd");
  } catch {
    return isoToday();
  }
};

const toDateTime = (dateStr, timeStr) => {
  const d = String(dateStr || "");
  const t = toHHMM(timeStr);
  if (!d || !t) return null;
  return new Date(`${d}T${t}:00`);
};

const calcDurationMin = (dateStr, startStr, endStr) => {
  const start = toDateTime(dateStr, startStr);
  const end = toDateTime(dateStr, endStr);
  if (!start || !end) return null;
  const diff = Math.round((end.getTime() - start.getTime()) / 60000);
  return Number.isFinite(diff) ? diff : null;
};

const PriseRDVAgenda = () => {
  useScrollToTop();

  const { language } = useContext(LanguageContext);

  const i18n = useMemo(
    () => ({
      FR: {
        pageTitle: "Prise de Rendez-vous",
        subtitle: "Choisissez une date disponible, puis sélectionnez un créneau horaire.",
        availableOnly: "Disponibles uniquement",
        allSlots: "Tous les créneaux",
        dateFrom: "Du",
        dateTo: "Au",
        location: "Lieu",
        anyLocation: "Tous",
        dayPart: "Moment",
        anyDayPart: "Toute la journée",
        morning: "Matin",
        afternoon: "Après-midi",
        evening: "Soir",
        refresh: "Rafraîchir",
        loading: "Chargement des créneaux…",
        emptyDates: "Aucune date disponible ne correspond aux filtres.",
        emptySlots: "Aucun créneau pour cette date avec les filtres.",
        reserved: "Réservé",
        available: "Disponible",
        book: "Réserver",
        toastReserved: "Ce créneau est déjà réservé.",
        loadError: "Erreur lors du chargement des créneaux.",
        availableCount: "disponible(s)",
        backToDates: "Retour aux dates",
        pickDate: "Choisir une date",
        quick7: "7 jours",
        quick14: "14 jours",
        quick30: "30 jours",
        quickAll: "Tout",
        locale: fr,
      },
      EN: {
        pageTitle: "Book an Appointment",
        subtitle: "Choose an available date, then pick a time slot.",
        availableOnly: "Available only",
        allSlots: "All slots",
        dateFrom: "From",
        dateTo: "To",
        location: "Location",
        anyLocation: "All",
        dayPart: "Time",
        anyDayPart: "All day",
        morning: "Morning",
        afternoon: "Afternoon",
        evening: "Evening",
        refresh: "Refresh",
        loading: "Loading slots…",
        emptyDates: "No available date matches your filters.",
        emptySlots: "No slots for this date with your filters.",
        reserved: "Booked",
        available: "Available",
        book: "Book",
        toastReserved: "This slot is already booked.",
        loadError: "Error while loading slots.",
        availableCount: "available",
        backToDates: "Back to dates",
        pickDate: "Pick a date",
        quick7: "7 days",
        quick14: "14 days",
        quick30: "30 days",
        quickAll: "All",
        locale: enGB,
      },
    }),
    []
  );

  const t = i18n[language] ?? i18n.FR;

  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedRDV, setSelectedRDV] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // filtres
  const [availableOnly, setAvailableOnly] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => isoToday());
  const [dateTo, setDateTo] = useState(() => toISODateSafe(addDays(new Date(), 30)));
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [dayPart, setDayPart] = useState("ALL"); // ALL | MORNING | AFTERNOON | EVENING

  // UX : date sélectionnée (on affiche ensuite les créneaux)
  const [openDayISO, setOpenDayISO] = useState(null);

  /* =========================
     STYLE HELPERS (DARK OK)
     ========================= */
  const softCard =
    "rounded-3xl border border-black/10 bg-[#E8BB89]/80 shadow-sm backdrop-blur-md " +
    "dark:bg-slate-950/45 dark:border-white/10 dark:shadow-none";

  const softText = "text-slate-900 dark:text-slate-50";
  const mutedText = "text-slate-700 dark:text-slate-300";
  const faintText = "text-slate-600 dark:text-slate-400";

  const iconPrimary = "text-primary/90 dark:text-primary";
  const iconMuted = "text-slate-500 dark:text-slate-300";

  const chip =
    "inline-flex items-center justify-center rounded-2xl px-3 py-1.5 text-sm font-semibold " +
    "border border-black/10 dark:border-white/10 " +
    "bg-white/70 dark:bg-slate-950/55 " +
    "text-slate-900 dark:text-slate-100 " +
    "hover:bg-white/90 dark:hover:bg-slate-950/70 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 " +
    "focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-950 " +
    "transition";

  const input =
    "w-full rounded-2xl px-3 py-2 text-sm outline-none transition " +
    "border border-black/10 dark:border-white/10 " +
    "bg-white/80 dark:bg-slate-950/55 " +
    "text-slate-900 dark:text-slate-100 " +
    "placeholder:text-slate-500 dark:placeholder:text-slate-500 " +
    "focus:ring-2 focus:ring-primary/70 focus:border-transparent " +
    "dark:[color-scheme:dark]";

  const dayCardBtn =
    "group w-full text-left rounded-3xl border px-5 py-4 transition " +
    "border-black/10 dark:border-white/10 " +
    "bg-white/65 dark:bg-slate-950/45 " +
    "hover:bg-white/90 dark:hover:bg-slate-950/65 " +
    "hover:border-primary/30";

  const slotBtnBase =
    "group w-full rounded-2xl border px-4 py-3 text-left transition " +
    "flex items-center justify-between gap-3 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 " +
    "focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-950";

  const pillAvailable =
    "inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold " +
    "border border-primary/25 bg-primary/10 text-primary " +
    "dark:border-primary/35 dark:bg-primary/15 dark:text-primary";

  const pillReserved =
    "inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold " +
    "border border-black/10 bg-black/5 text-slate-700 " +
    "dark:border-white/10 dark:bg-white/5 dark:text-slate-300";

  /* =========================
     DATA
     ========================= */
  const fetchRDV = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllRDV();

      const normalized = (data || [])
        .map((rdv) => {
          const isReserved = !!rdv.nom_client;
          const startDt = toDateTime(rdv.date_rdv, rdv.heure_debut);
          const endDt = toDateTime(rdv.date_rdv, rdv.heure_fin);
          const durationMin = calcDurationMin(rdv.date_rdv, rdv.heure_debut, rdv.heure_fin);

          return {
            ...rdv,
            id: String(rdv.id),
            isReserved,
            startDt,
            endDt,
            durationMin,
            startHHMM: toHHMM(rdv.heure_debut),
            endHHMM: toHHMM(rdv.heure_fin),
            dateISO: String(rdv.date_rdv || ""),
            location: rdv.lieu || "",
          };
        })
        .filter((x) => x.dateISO)
        .sort((a, b) => {
          const ta = a.startDt ? a.startDt.getTime() : 0;
          const tb = b.startDt ? b.startDt.getTime() : 0;
          return ta - tb;
        });

      setRdvs(normalized);
    } catch (e) {
      console.error(e);
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  }, [t.loadError]);

  useEffect(() => {
    fetchRDV();
  }, [fetchRDV]);

  const locations = useMemo(() => {
    const set = new Set();
    rdvs.forEach((r) => {
      if (r.location) set.add(r.location);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rdvs]);

  const inDayPart = useCallback(
    (slot) => {
      const h = Number(String(slot.startHHMM || "00:00").slice(0, 2));
      if (!Number.isFinite(h)) return true;
      if (dayPart === "MORNING") return h >= 6 && h < 12;
      if (dayPart === "AFTERNOON") return h >= 12 && h < 18;
      if (dayPart === "EVENING") return h >= 18 && h <= 23;
      return true;
    },
    [dayPart]
  );

  const baseFiltered = useMemo(() => {
    const from = dateFrom || "0000-00-00";
    const to = dateTo || "9999-12-31";

    return rdvs.filter((slot) => {
      if (slot.dateISO < from || slot.dateISO > to) return false;
      if (locationFilter !== "ALL" && slot.location !== locationFilter) return false;
      if (!inDayPart(slot)) return false;
      return true;
    });
  }, [rdvs, dateFrom, dateTo, locationFilter, inDayPart]);

  const days = useMemo(() => {
    const map = new Map();

    baseFiltered.forEach((slot) => {
      const key = slot.dateISO;
      if (!map.has(key)) map.set(key, { dateISO: key, allSlots: [], availableCount: 0 });
      const entry = map.get(key);
      entry.allSlots.push(slot);
      if (!slot.isReserved) entry.availableCount += 1;
    });

    for (const entry of map.values()) {
      entry.allSlots.sort((a, b) => (a.startDt?.getTime() || 0) - (b.startDt?.getTime() || 0));
    }

    return Array.from(map.values())
      .filter((d) => d.availableCount > 0)
      .sort((a, b) => a.dateISO.localeCompare(b.dateISO));
  }, [baseFiltered]);

  useEffect(() => {
    if (!openDayISO) return;
    const exists = days.some((d) => d.dateISO === openDayISO);
    if (!exists) setOpenDayISO(null);
  }, [days, openDayISO]);

  const totalAvailable = useMemo(() => {
    return baseFiltered.reduce((acc, s) => acc + (s.isReserved ? 0 : 1), 0);
  }, [baseFiltered]);

  const openBooking = (slot) => {
    if (slot.isReserved) {
      toast.info(t.toastReserved);
      return;
    }
    setSelectedRDV(slot);
    setIsModalOpen(true);
  };

  const formatDayHeader = (dateISO) => {
    try {
      return format(parseISO(dateISO), "EEEE d MMMM yyyy", { locale: t.locale });
    } catch {
      return dateISO;
    }
  };

  return (
    <div className={`relative min-h-screen pb-12 ${softText}`}>
      {/* Décor */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-[60px] left-0 w-full h-[33vh] bg-cover bg-no-repeat bg-bottom opacity-70 dark:opacity-20"
          style={{ backgroundImage: "url('/images/fond_sakura.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 via-white/10 to-transparent dark:from-black/80 dark:via-black/40 dark:to-black/20" />
      </div>

      <div className="relative z-10 max-w-[1280px] mx-auto px-4 pt-24 sm:pt-28">
        <div className="text-center">
          <h2 className="text-3xl sm:text-4xl font-satoshi font-extrabold tracking-tight">
            {t.pageTitle}
          </h2>
          <div className="mt-4 flex justify-center">
            <div className="h-[3px] w-28 rounded-full bg-primary/90" />
          </div>
          <p className={`mt-4 text-sm sm:text-base ${mutedText}`}>{t.subtitle}</p>
        </div>

        {/* FILTRES */}
        <div className={`${softCard} mt-8 p-4 sm:p-6`}>
          <div className="flex flex-col gap-4">
            {/* quick ranges */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={chip}
                onClick={() => {
                  setDateFrom(isoToday());
                  setDateTo(toISODateSafe(addDays(new Date(), 7)));
                }}
              >
                {t.quick7}
              </button>
              <button
                type="button"
                className={chip}
                onClick={() => {
                  setDateFrom(isoToday());
                  setDateTo(toISODateSafe(addDays(new Date(), 14)));
                }}
              >
                {t.quick14}
              </button>
              <button
                type="button"
                className={chip}
                onClick={() => {
                  setDateFrom(isoToday());
                  setDateTo(toISODateSafe(addDays(new Date(), 30)));
                }}
              >
                {t.quick30}
              </button>
              <button
                type="button"
                className={chip}
                onClick={() => {
                  setDateFrom("0000-01-01");
                  setDateTo("9999-12-31");
                }}
              >
                {t.quickAll}
              </button>

              <button type="button" className={`${chip} ml-auto`} onClick={fetchRDV} title={t.refresh}>
                <FaSyncAlt className={`mr-2 ${iconPrimary}`} />
                {t.refresh}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
              <div className="lg:col-span-3">
                <label className={`text-xs font-semibold ${faintText} flex items-center gap-2 mb-1`}>
                  <FaRegCalendarAlt className={iconMuted} /> {t.dateFrom}
                </label>
                <input type="date" className={input} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>

              <div className="lg:col-span-3">
                <label className={`text-xs font-semibold ${faintText} flex items-center gap-2 mb-1`}>
                  <FaRegCalendarAlt className={iconMuted} /> {t.dateTo}
                </label>
                <input type="date" className={input} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>

              <div className="lg:col-span-3">
                <label className={`text-xs font-semibold ${faintText} flex items-center gap-2 mb-1`}>
                  <FaMapMarkerAlt className={iconMuted} /> {t.location}
                </label>
                <select className={input} value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                  <option value="ALL">{t.anyLocation}</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>

              <div className="lg:col-span-3">
                <label className={`text-xs font-semibold ${faintText} flex items-center gap-2 mb-1`}>
                  <FaClock className={iconMuted} /> {t.dayPart}
                </label>
                <select className={input} value={dayPart} onChange={(e) => setDayPart(e.target.value)}>
                  <option value="ALL">{t.anyDayPart}</option>
                  <option value="MORNING">{t.morning}</option>
                  <option value="AFTERNOON">{t.afternoon}</option>
                  <option value="EVENING">{t.evening}</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-primary"
                  checked={availableOnly}
                  onChange={(e) => setAvailableOnly(e.target.checked)}
                />
                <span className="text-sm font-semibold">
                  {availableOnly ? t.availableOnly : t.allSlots}
                </span>
              </label>

              <div className={`text-sm ${mutedText}`}>
                <span className="font-extrabold text-primary">{totalAvailable}</span> {t.availableCount}
              </div>
            </div>
          </div>
        </div>

        {/* CONTENU : dates ou créneaux */}
        <div className="mt-6">
          {loading ? (
            <div className={`${softCard} p-6`}>
              <p className={`text-center ${mutedText}`}>{t.loading}</p>
            </div>
          ) : days.length === 0 ? (
            <div className={`${softCard} p-6`}>
              <p className={`text-center ${mutedText}`}>{t.emptyDates}</p>
            </div>
          ) : openDayISO ? (
            <div className={`${softCard} overflow-hidden`}>
              <div className="px-5 py-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className={`text-xs font-semibold ${faintText}`}>{t.pickDate}</div>
                  <h3 className="text-base sm:text-lg font-satoshi font-extrabold capitalize">
                    {formatDayHeader(openDayISO)}
                  </h3>
                </div>

                <button type="button" onClick={() => setOpenDayISO(null)} className={chip} title={t.backToDates}>
                  <FaArrowLeft className={`mr-2 ${iconPrimary}`} />
                  {t.backToDates}
                </button>
              </div>

              <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {(() => {
                  const day = days.find((d) => d.dateISO === openDayISO);
                  if (!day) return null;

                  const slots = availableOnly ? day.allSlots.filter((s) => !s.isReserved) : day.allSlots;

                  if (slots.length === 0) {
                    return (
                      <div className="md:col-span-2 xl:col-span-3">
                        <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-slate-950/40 p-6">
                          <p className={`text-center ${mutedText}`}>{t.emptySlots}</p>
                        </div>
                      </div>
                    );
                  }

                  return slots.map((slot) => {
                    const badgeText = slot.isReserved ? t.reserved : t.available;

                    const tone = slot.isReserved
                      ? "border-black/10 dark:border-white/10 bg-white/55 dark:bg-slate-950/35 opacity-70"
                      : "border-primary/25 bg-white/70 dark:bg-slate-950/45 hover:bg-white/95 dark:hover:bg-slate-950/65 hover:border-primary/50";

                    return (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => openBooking(slot)}
                        className={`${slotBtnBase} ${tone}`}
                        disabled={slot.isReserved}
                        title={slot.isReserved ? badgeText : t.book}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold">
                            {slot.startHHMM} – {slot.endHHMM}
                            {slot.durationMin ? (
                              <span className={`ml-2 text-xs font-semibold ${mutedText}`}>
                                ({slot.durationMin} min)
                              </span>
                            ) : null}
                          </div>

                          <div className={`mt-1 text-xs ${mutedText} truncate`}>
                            {slot.location ? (
                              <>
                                <FaMapMarkerAlt className={`inline mr-2 ${iconMuted}`} />
                                {slot.location}
                              </>
                            ) : (
                              "—"
                            )}
                          </div>
                        </div>

                        <div className="shrink-0 flex items-center gap-2">
                          <span className={slot.isReserved ? pillReserved : pillAvailable}>{badgeText}</span>
                          {!slot.isReserved && (
                            <span className="text-primary text-sm font-extrabold group-hover:translate-x-0.5 transition-transform">
                              →
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {days.map((d) => (
                <button key={d.dateISO} type="button" className={dayCardBtn} onClick={() => setOpenDayISO(d.dateISO)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`text-xs font-semibold ${faintText} flex items-center gap-2`}>
                        <FaRegCalendarAlt className={iconMuted} />
                        {formatDayHeader(d.dateISO)}
                      </div>

                      <div className={`mt-2 text-sm ${mutedText}`}>
                        <span className="font-extrabold text-primary">{d.availableCount}</span> {t.availableCount}
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-2">
                      <span className={pillAvailable}>{t.book}</span>
                      <FaChevronRight className={`text-primary/90 dark:text-primary group-hover:translate-x-0.5 transition-transform`} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <ReservationRDVModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        rdvToBook={selectedRDV}
        refreshList={fetchRDV}
      />

      <ToastContainer />
    </div>
  );
};

export default PriseRDVAgenda;
